import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// icons
import {
  ArrowUpwardOutlined,
  AttachFileOutlined,
  CloseRounded,
  EditOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ViewModuleOutlined,
} from "@mui/icons-material";
import { Box, Button, Checkbox } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, size } from "lodash";
import isNil from "lodash/isNil";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import FileUploadField from "../../../../components/inputs/FileUploadField";
import SearchInput from "../../../../components/inputs/SearchInput";
import SelectFileAssignmentInput from "../../../../components/inputs/SelectFileAssignmentInput";
import InputWrapper from "../../../../components/inputs/shared/InputWrapper";
import Flx from "../../../../components/layout/Flx";
import FilterTabSwitcher from "../../../../components/navigation/FilterTabSwitcher";
import Txt from "../../../../components/typography/Txt";
import { useFileDocGroupAndTypesHook } from "../../../../hooks/useFileDocGroupAndTypesHook";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import LoanDocumentManagerTable from "./LoanDocumentManagerTable";

const UPLOAD_TYPES = {
  QUICK_UPLOAD: "quick_upload",
  GROUP: "group",
};

const LoanDrilldownDocumentsTab = ({ value }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loan?.loanDocuments;
  const [documentStatusFilter, setDocumentStatusFilter] = useState("All");
  const [uploadType, setUploadType] = useState(UPLOAD_TYPES.QUICK_UPLOAD);
  const [tab, setTab] = useState("All");

  const filteredDocuments = useMemo(() => {
    if (documentStatusFilter === "All") {
      return loanDocuments;
    }
    if (documentStatusFilter === "Missing") {
      return loanDocuments?.filter((c) => c?.documentStatus !== "Accepted");
    }

    return loanDocuments?.filter(
      (c) => c.documentStatus === documentStatusFilter,
    );
  }, [loanDocuments, documentStatusFilter]);

  return (
    <>
      <PrimaryContentCard
        maxWidth={"xl"}
        subTitle={"Upload files freely or organize them by category"}
        title="Documents"
        endContent={
          <FilterTabSwitcher
            fontSize={11}
            value={uploadType}
            sx={{
              background: "#f2f1ef",
              border: "1px solid #e5e3df",
              // borderRadius: "4px",
            }}
            onChange={setUploadType}
            tabs={[
              {
                label: "Quick Upload",
                value: UPLOAD_TYPES.QUICK_UPLOAD,
                icon: <ArrowUpwardOutlined />,
              },
              {
                label: "Upload By Category",
                value: UPLOAD_TYPES.GROUP,
                icon: <ViewModuleOutlined />,
              },
            ]}
          />
        }
      >
        <LoanDocumentUploaderProcess view={uploadType} />
        {/* <Divider sx={{ mt: 4, mb: 4, borderWidth: "1.5px" }} /> */}
        {isEmpty(loanDocuments) ? (
          <Flx sx={{ py: 2 }}>
            <Txt sx={{ fontStyle: "italic" }}>
              No documents added yet for this loan.
            </Txt>
          </Flx>
        ) : (
          <Flx column>
            <Flx g={1} fw jb ac wrap sx={{ mb: 2 }}>
              <Flx g={0.2} column>
                <Txt bold>Uploaded Loan Documents ({size(loanDocuments)})</Txt>
                <Txt secondary>
                  Click on any of the following document cards to edit.
                </Txt>
              </Flx>
              <LoanDocumentStatusSwitcher
                value={documentStatusFilter}
                onChange={setDocumentStatusFilter}
              />
            </Flx>
            <LoanDocumentManagerTable
              rowData={filteredDocuments}
              // onRowClicked={onEditClick}
            />
          </Flx>
        )}
      </PrimaryContentCard>
    </>
  );
};

const DOC_STATUS_FILTER_TYPES = {
  ALL: "All",
  MISSING: "Missing",
  NEW_UPLOAD: "New Upload",
  PENDING_REVIEW: "Pending Review",
  INCOMPLETE: "Incomplete",
  REJECTED: "Rejected",
  ACCEPTED: "Accepted",
};

const LoanDocumentStatusSwitcher = ({ value, onChange }) => {
  const loan = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loan?.loanDocuments;
  const tabs = useMemo(
    () => [
      {
        label: "All",
        value: DOC_STATUS_FILTER_TYPES.ALL,
        count: loanDocuments?.length || 0,
      },
      // {
      //   label: "Missing",
      //   value: DOC_STATUS_FILTER_TYPES.MISSING,
      //   count:
      //     loanDocuments?.filter((d) => d.documentStatus === "Missing").length ||
      //     0,
      // },
      {
        label: "New Upload",
        value: DOC_STATUS_FILTER_TYPES.NEW_UPLOAD,
        count:
          loanDocuments?.filter((d) => d.documentStatus === "New Upload")
            .length || 0,
      },
      {
        label: "Pending Review",
        value: DOC_STATUS_FILTER_TYPES.PENDING_REVIEW,
        count:
          loanDocuments?.filter((d) => d.documentStatus === "Pending Review")
            .length || 0,
      },
      {
        label: "Accepted",
        value: DOC_STATUS_FILTER_TYPES.ACCEPTED,
        count:
          loanDocuments?.filter((d) => d.documentStatus === "Accepted")
            .length || 0,
      },
      {
        label: "Incomplete",
        value: DOC_STATUS_FILTER_TYPES.INCOMPLETE,
        count:
          loanDocuments?.filter((d) => d.documentStatus === "Incomplete")
            .length || 0,
      },
      {
        label: "Rejected",
        value: DOC_STATUS_FILTER_TYPES.REJECTED,
        count:
          loanDocuments?.filter((d) => d.documentStatus === "Rejected")
            .length || 0,
      },
    ],
    [loanDocuments],
  );
  // const tabs = useMemo(
  //   () => [{
  //     label: "All",
  //     value: "All",
  //     count: loanDocuments?.length || 0,
  //   },
  // {
  //     label: "Missing",
  //     value: "Missing",
  //     count: loanDocuments?.filter((d) => d.status === "Missing").length || 0,
  // }],
  //   [loanDocuments],
  // );

  return (
    <FilterTabSwitcher
      value={value}
      fontSize={11}
      onChange={onChange}
      tabs={tabs}
    />
  );
};

const LoanDocumentUploaderProcess = ({ view }) => {
  const [addedFiles, setAddedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, uploadLoanDocs } = useLoanFilesHook();
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);

  const onFilesAdded = useCallback((newFiles) => {
    setAddedFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const onCancelEditing = () => {
    setSelectedFiles([]);
    setEditing(false);
  };

  const onDocGroupTypeChange = useCallback(
    (p) => {
      const { docGroup, docType } = p || {};
      console.log("docGroup, docType", docGroup, docType);
      if (!docGroup && !docType) return;
      console.log("addedFiles", addedFiles);
      console.log("selectedFiles", { selectedFiles, addedFiles });
      // selectedFiles should be an array of keys (or ids)
      const selectedSet = new Set(selectedFiles.map((f) => f.id));

      const updated = addedFiles.map((file) => {
        const key = file?.id;

        if (!selectedSet.has(key)) return file;
        return {
          ...file,
          docGroup,
          docType,
        };
      });
      console.log("updated files with new docGroup/docType", updated);
      setAddedFiles(updated);

      // close editing UI after applying change (matches your current intent)
      setSelectedFiles([]);
      setEditing(false);
    },
    [addedFiles, selectedFiles],
  );

  const handleUploadFiles = () => {
    // console.log("Uploading files:", addedFiles);
    // return;
    uploadLoanDocs({
      loanId: loanDrilldown?._id,
      files: addedFiles,
      onSuccessMsg:
        size(addedFiles) > 1
          ? `${size(addedFiles)} files uploaded successfully`
          : "File uploaded successfully",
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        setAddedFiles([]);
      },
    });
  };

  const border = `1px solid ${grey[300]}`;
  const onRemoveFile = useCallback((fileId) => {
    setAddedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  return (
    <Flx
      // g={4}
      // wrap
      column
      sx={{
        // overflow: "visible",
        position: "relative",

        borderTop: isEmpty(addedFiles) ? "none" : border,
        borderRight: isEmpty(addedFiles) ? "none" : border,
        borderLeft: isEmpty(addedFiles) ? "none" : border,
        borderBottom: border,
        p: isEmpty(addedFiles) ? 0 : 2,
        borderRadius: isEmpty(addedFiles) ? 0 : 4,
        pb: 5,
        mb: 5,
      }}
    >
      {view === UPLOAD_TYPES.QUICK_UPLOAD && (
        <FileUploadField
          key={""}
          onFilesAdded={onFilesAdded}
          suppressGrid
          borderSize={2}
          background={"#ffffff"}
          suppressFileList
          minHeight={"120px"}
          // uploadText={dt}
          suppressUploadButton
        />
      )}
      {view === UPLOAD_TYPES.GROUP && (
        <UploadLoanDocumentByGroupProcess
          addedFiles={addedFiles}
          onFilesAdded={onFilesAdded}
          onRemoveFile={onRemoveFile}
        />
      )}

      {!isEmpty(addedFiles) && (
        <>
          {/* <Divider
            orientation="vertical"
            // flexItem
            // sx={{ borderWidth: "2px" }}
          /> */}

          <Flx
            column
            sx={{
              // gap: 1,
              // position: "sticky",
              // top: 0,

              // flexBasis: "540px",
              borderTop: "2px solid #eeeeee",
              px: 1,
              mt: 4,
              pt: 4,
              // borderRadius: 2,
              // borderLeft: "3px solid #eeeeee",
              // pl: 3,
              // flexGrow: 1,
              zIndex: 111,
              // height: "100%",
              height: "unset",
              // height: "max-content",
              // m: 2,
              // pl: 4,
            }}
          >
            <Flx
              column
              sx={{
                background: "#f2f1ef",
                p: 2,
                border: "1px solid #e5e3df",
                borderBottom: 0,
                borderTopRightRadius: "8px",
                borderTopLeftRadius: "8px",
              }}
            >
              <Txt bold fontSize={16}>
                Pending Files ({size(addedFiles)})
              </Txt>
              {/* <Txt bold>Files to be uploaded ({size(addedFiles)})</Txt> */}
              <Txt secondary>
                Review the following files before uploading. Click on any added
                file below to edit its name
              </Txt>
            </Flx>
            <Flx column g={2.5} sx={{ mb: 1 }}>
              <UploadedFilesTable
                editing={editing}
                files={addedFiles}
                setFiles={setAddedFiles}
                setSelectedFiles={setSelectedFiles}
              />
              {editing && !isEmpty(selectedFiles) && (
                <InputWrapper sx={{ mb: 2 }} label={"Select Document Type"}>
                  <SelectFileAssignmentInput onChange={onDocGroupTypeChange} />
                </InputWrapper>
              )}
            </Flx>
            <Flx
              g={1}
              fw
              ac
              jb
              sx={{
                mt: 2,
              }}
            >
              <Button
                onClick={() => setAddedFiles([])}
                variant="outlined"
                size="large"
                color="error"
              >
                Clear All
              </Button>
              <Flx g={1} fw ac end wrap>
                <Button
                  onClick={() => setEditing((s) => !s)}
                  variant="outlined"
                  size="large"
                  color="gray"
                >
                  {editing ? "Cancel Editing" : "Edit Document Types"}
                </Button>
                <Button
                  loading={loading}
                  onClick={() => handleUploadFiles(addedFiles)}
                  size="large"
                  color="primary"
                >
                  Upload Files ({size(addedFiles)})
                </Button>
              </Flx>
            </Flx>
          </Flx>
        </>
      )}
    </Flx>
  );
};

const UploadedFilesTable = ({ files, setFiles, setSelectedFiles, editing }) => {
  const ref = useRef();
  const modalContentRef = useRef(null);

  const groupRows = useMemo(() => {
    const uniqueGroups = new Set(files.map((f) => f.docGroup));
    return uniqueGroups.size > 1;
  }, [files]);

  const handleToggleParse = useCallback(
    (id, checked) => {
      setFiles((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, parse: checked } : item,
        ),
      );

      setTimeout(() => {
        if (ref.current?.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setFiles],
  );

  const columnDefs = useMemo(
    () => [
      {
        resizable: false,
        cellRenderer: () => {
          return <AttachFileOutlined className="thin" sx={{ fontSize: 22 }} />;
        },
        maxWidth: 32,
        cellStyle: {
          paddingLeft: 0,
          paddingRight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 150,
        tooltipValueGetter: (p) => {
          return `Rename ${p?.data?.file_display_name}`;
        },
        cellEditor: "agTextCellEditor",
        editable: true,
        cellRenderer: (params) => {
          return (
            <Flx fw jb ac>
              <Flx column>
                <Txt fontSize={12}>{params.value}</Txt>
                {!params?.data?.docGroup || !params?.data?.docType ? null : (
                  <Txt className="thin" secondary sx={{ fontSize: 10 }}>
                    {params?.data?.docType}
                  </Txt>
                )}
              </Flx>
              <EditOutlined className="thin" color="grey" />
            </Flx>
          );
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
          cursor: "text",
          gap: "6px",
          paddingLeft: "0px",
        },
      },
      {
        field: "docGroup",
        rowGroup: groupRows,
        hide: true,
        valueGetter: (params) => {
          return params?.data?.docGroup || "Uncategorized";
        },
      },
      // {
      //   field: "parse",
      //   width: 110,
      //   flex: 0,
      //   editable: false,
      //   cellRenderer: (params) => {
      //     if (params.node.group) {
      //       return null;
      //     }

      //     return (
      //       <Flx
      //         center
      //         sx={{
      //           height: "100%",
      //           width: "100%",
      //         }}
      //         onClick={(event) => {
      //           event.stopPropagation();
      //         }}
      //       >
      //         <Flx
      //           g={1}
      //           sx={{
      //             border: `1px solid ${grey[200]}`,
      //             borderRadius: "50px",
      //             px: 1.2,
      //             py: 0.4,
      //           }}
      //         >
      //           <Txt fontSize={12}>Parse</Txt>
      //           <Checkbox
      //             sx={{ p: 0, pointerEvents: "none" }}
      //             checked={Boolean(params.value)}
      //             size="small"
      //             disableRipple
      //           />
      //         </Flx>
      //       </Flx>
      //     );
      //   },
      //   cellStyle: {
      //     display: "flex",
      //     alignItems: "center",
      //     justifyContent: "center",
      //     cursor: "pointer",
      //   },
      // },
      // {
      //   field: "parse",
      //   width: 110,
      //   flex: 0,
      //   editable: false,
      //   cellRenderer: (params) => {
      //     if (params.node.group) {
      //       return null;
      //     }

      //     return (
      //       <Flx
      //         center
      //         sx={{
      //           height: "100%",
      //           width: "100%",
      //         }}
      //         onClick={(event) => {
      //           event.stopPropagation();
      //         }}
      //       >
      //         <Flx
      //           g={1}
      //           sx={{
      //             border: `1px solid ${grey[200]}`,
      //             borderRadius: "50px",
      //             px: 1.2,
      //             py: 0.4,
      //           }}
      //         >
      //           <Txt fontSize={12}>Parse</Txt>
      //           <Checkbox
      //             sx={{ p: 0, pointerEvents: "none" }}
      //             checked={Boolean(params.value)}
      //             size="small"
      //             disableRipple
      //           />
      //         </Flx>
      //       </Flx>
      //     );
      //   },
      //   cellStyle: {
      //     display: "flex",
      //     alignItems: "center",
      //     justifyContent: "center",
      //     cursor: "pointer",
      //   },
      // },
      {
        width: 38,
        headerName: "",
        pinned: "right",
        cellStyle: {
          paddingRight: 0,
          paddingLeft: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        },
        field: "delete",
        cellRenderer: () => {
          return <CloseRounded className="thin" color="error" />;
        },
      },
    ],
    [groupRows, handleToggleParse],
  );

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;
      const updatedData = { ...data };

      if (colDef.field === "file_display_name") {
        const originalExtension = data.name.split(".").pop().toLowerCase();
        const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
        updatedData.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
      }

      if (colDef.field === "docGroup") {
        updatedData.docGroup = newValue;
        updatedData.docType = "";
      }

      if (colDef.field === "docType") {
        updatedData.docType = newValue;
      }

      setFiles((prev) =>
        prev.map((item) => (item.id === data.id ? updatedData : item)),
      );

      setTimeout(() => {
        if (ref.current?.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setFiles],
  );

  const rowSelection = useMemo(() => {
    if (editing) {
      return { mode: "multiRow" };
    }

    return {};
  }, [editing]);

  const onCellClicked = useCallback(
    (params) => {
      const { colDef, data } = params;

      if (colDef.field === "delete") {
        setFiles((prev) => prev.filter((item) => item.id !== data.id));
        return;
      }

      if (colDef.field === "parse" && !params.node.group) {
        setFiles((prev) =>
          prev.map((item) =>
            item.id === data.id ? { ...item, parse: !item.parse } : item,
          ),
        );

        setTimeout(() => {
          if (ref.current?.api) {
            ref.current.api.refreshCells({
              rowNodes: [params.node],
              columns: ["parse"],
            });
          }
        }, 0);
      }
    },
    [setFiles],
  );

  const handleRowSelectionChange = useCallback(
    (params) => {
      const selected = params.api.getSelectedRows();
      setSelectedFiles(selected);
    },
    [setSelectedFiles],
  );

  const onRowClicked = useCallback((params) => {
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
    }
  }, []);

  const getRowStyle = useCallback((params) => {
    if (params.node.group) {
      return {
        fontWeight: 600,
        fontStyle: "italic",
        fontSize: "13px",
        cursor: "pointer",
      };
    }

    return undefined;
  }, []);

  if (!files?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        border: "1px solid #e5e3df",
        // border: "1px solid #eeeeee",
        overflow: "hidden",
        borderRadius: "8px",
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        ".ag-header": {
          border: "none !important",
        },
        ".ag-pinned-right-cols-container .ag-cell": {
          borderLeft: "1px solid #eeeeee !important",
        },
        ".ag-row-last": {
          borderBottom: "none !important",
        },
      }}
    >
      <AgGridTableWrapper simpleTable suppressMinHeightWhenPopulated={files}>
        <div ref={modalContentRef}>
          <AgGridReact
            ref={ref}
            rowData={files}
            columnDefs={columnDefs}
            singleClickEdit
            rowHeight={54}
            onCellClicked={onCellClicked}
            domLayout="autoHeight"
            tooltipShowDelay={100}
            tooltipMouseTrack
            headerHeight={0}
            getRowStyle={getRowStyle}
            onRowClicked={onRowClicked}
            rowSelection={rowSelection}
            onSelectionChanged={handleRowSelectionChange}
            groupDisplayType="groupRows"
            groupDefaultExpanded={groupRows ? 1 : -1}
            onCellValueChanged={handleCellValueChanged}
            stopEditingWhenCellsLoseFocus
            popupParent={modalContentRef.current}
          />
        </div>
      </AgGridTableWrapper>
    </Box>
  );
};

// In UploadLoanDocumentByGroupProcess — add onRemoveFile handler and pass it down

const UploadLoanDocumentByGroupProcess = ({
  addedFiles,
  onFilesAdded,
  onRemoveFile,
}) => {
  const { fileDocGroups, getDocTypesByGroup } = useFileDocGroupAndTypesHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [search, setSearch] = useState("");

  const [openGroups, setOpenGroups] = useState(new Set());

  const filteredGroups = useMemo(() => {
    if (!search?.trim()) return fileDocGroups;
    const lower = search.toLowerCase();
    return fileDocGroups.filter((gr) =>
      getDocTypesByGroup(gr)?.some((dt) => dt.toLowerCase().includes(lower)),
    );
  }, [fileDocGroups, search, getDocTypesByGroup]);

  const allExpanded = filteredGroups.every((gr) => openGroups.has(gr));

  const handleExpandAll = () => setOpenGroups(new Set(filteredGroups));
  const handleCollapseAll = () => setOpenGroups(new Set());

  return (
    <Flx column g={1}>
      <Flx ac fw g={1} sx={{ position: "sticky", top: 0, left: 0, zIndex: 11 }}>
        <SearchInput
          sx={{ flexGrow: 1, mr: 3 }}
          value={search}
          placeholder="Search for a document type..."
          onChange={setSearch}
        />
        <Button
          size="small"
          startIcon={<KeyboardArrowUp fontSize="small" />}
          variant="outlined"
          color="gray"
          onClick={handleExpandAll}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Open All Groups
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="gray"
          startIcon={<KeyboardArrowDown fontSize="small" />}
          onClick={handleCollapseAll}
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Close All Groups
        </Button>
      </Flx>
      {/* <FlxGrid width="260px"> */}
      <FlxGrid width="210px">
        {isEmpty(filteredGroups) && (
          <Txt sx={{ fontStyle: "italic", p: 1 }}>No document types found.</Txt>
        )}
        {filteredGroups.map((gr) => (
          <DocGroupUpload
            key={gr}
            add
            docGroup={gr}
            search={search}
            addedFiles={addedFiles}
            isOpen={openGroups.has(gr)}
            onToggle={(group) =>
              setOpenGroups((prev) => {
                const next = new Set(prev);
                next.has(group) ? next.delete(group) : next.add(group);
                return next;
              })
            }
            onFilesAdded={onFilesAdded}
            onRemoveFile={onRemoveFile}
          />
        ))}
      </FlxGrid>
    </Flx>
  );
};
// DocGroupUpload — pass addedFiles and onRemoveFile to DocTypeUploadList

const DocGroupUpload = ({
  docGroup,
  onFilesAdded,
  onRemoveFile,
  addedFiles,
  search,
  isOpen,
  onToggle,
}) => {
  const isSearching = !!search?.trim();
  const expanded = isSearching || isOpen;

  const filteredGroupCount = size(
    addedFiles?.filter((f) => f?.docGroup === docGroup),
  );
  return (
    <Flx
      column
      sx={{
        bgcolor: "#f2f1ef",
        border: "1px solid #e5e3df",
        borderRadius: 1,
        height: "max-content",
      }}
    >
      <Box
        onClick={() => onToggle(docGroup)}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
          p: 1,
        }}
      >
        <Txt
          sx={{ fontSize: "11.5px", fontWeight: 500 }}
        >{`${docGroup} ${filteredGroupCount > 0 ? `(${filteredGroupCount})` : ""}`}</Txt>
        {expanded ? (
          <KeyboardArrowUp fontSize="small" />
        ) : (
          <KeyboardArrowDown fontSize="small" />
        )}
      </Box>
      <DocTypeUploadList
        docGroup={docGroup}
        show={expanded}
        search={search}
        onFilesAdded={onFilesAdded}
        addedFiles={addedFiles}
        onRemoveFile={onRemoveFile}
      />
    </Flx>
  );
};

// DocTypeUploadList — show files under each docType

const DocTypeUploadList = ({
  docGroup,
  onFilesAdded,
  onRemoveFile,
  show,
  search,
  addedFiles,
}) => {
  const { getDocTypesByGroup } = useFileDocGroupAndTypesHook();

  const docTypes = getDocTypesByGroup(docGroup) || [];

  const filteredDocTypes = useMemo(() => {
    if (!search?.trim()) return docTypes;
    return docTypes.filter((dt) =>
      dt.toLowerCase().includes(search.toLowerCase()),
    );
  }, [docTypes, search]);

  const minHeight = useMemo(() => {
    return "44px";
  }, [docTypes]);

  const gridWidth = useMemo(() => {
    return "185px";
    // return "105px";
  }, [docTypes]);

  // Group addedFiles by docType for quick lookup
  const filesByDocType = useMemo(() => {
    const map = {};
    addedFiles?.forEach((f) => {
      if (f?.docGroup === docGroup && f?.docType) {
        if (!map[f.docType]) map[f.docType] = [];
        map[f.docType].push(f);
      }
    });
    return map;
  }, [addedFiles, docGroup]);

  return (
    <Box sx={{ overflow: "hidden", height: show ? "auto" : 0 }}>
      <FlxGrid
        width={gridWidth}
        sx={{
          p: 1,
          borderTop: "1px solid #e5e3df",
          ".upload-text-label": {
            fontSize: "11.5px",
            // fontSize: "10.5px",
            opacity: 1,
            // letterSpacing: "-.2px",
          },
          ".file-upload-field": {
            py: 1.1,
          },
        }}
      >
        {filteredDocTypes.map((dt) => {
          const dtFiles = filesByDocType[dt] || [];
          return (
            <Flx
              key={dt}
              column
              sx={{
                ".file-upload-field": {
                  // borderBottomStyle: size(dtFiles) >= 1 ? "solid" : "dashed",
                  borderBottomWidth: size(dtFiles) >= 1 ? 0 : 2,
                },
              }}
            >
              <FileUploadField
                onFilesAdded={(f) => onFilesAdded(f, docGroup, dt)}
                suppressGrid
                hideUploadButton
                docType={dt}
                borderSize={2}
                docGroup={docGroup}
                background={"#ffffff"}
                suppressFileList
                minHeight={minHeight}
                uploadText={dt}
                suppressUploadButton
              />
              {dtFiles.length > 0 && (
                <Flx
                  column
                  sx={{
                    // mt: 0.5,
                    mb: 0.5,
                    gap: 0.25,
                    bgcolor: "#fff",
                    // border: "1px solid #e5e3df",
                    border: "1px solid #e0e0e0",
                    borderTop: "1px solid #e0e0e094",

                    mt: "-2px",
                    // mx: 0.6,
                    // borderRadius: "8px",
                    // borderTop: "none",
                    borderBottomLeftRadius: "6px",
                    borderBottomRightRadius: "6px",
                  }}
                >
                  {dtFiles.map((file) => (
                    <Flx
                      key={file.id}
                      ac
                      jb
                      sx={{
                        borderRadius: 0.5,
                        px: 0.5,
                        py: 0.75,
                        gap: 0.5,
                      }}
                    >
                      <Flx ac g={0.5} sx={{ overflow: "hidden", minWidth: 0 }}>
                        <AttachFileOutlined
                          sx={{ fontSize: 12, color: grey[500], flexShrink: 0 }}
                        />
                        <Txt
                          sx={{
                            fontSize: "10px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontStyle: "italic",
                          }}
                        >
                          {file.file_display_name || file.name}
                        </Txt>
                      </Flx>
                      <CloseRounded
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveFile(file.id);
                        }}
                        sx={{
                          fontSize: 14,
                          color: red[600],
                          cursor: "pointer",
                          flexShrink: 0,
                          "&:hover": { color: "error.main" },
                        }}
                      />
                    </Flx>
                  ))}
                </Flx>
              )}
            </Flx>
          );
        })}
      </FlxGrid>
    </Box>
  );
};

const buildStatusFilterTabs = (conditions = []) => {
  if (isNil(conditions)) return [];
  const counts = {};
  conditions.forEach((c) => {
    const status = c.status || "Unknown";
    counts[status] = (counts[status] || 0) + 1;
  });

  const tabs = [{ label: "All", value: "All", count: conditions.length }];
  Object.entries(counts).forEach(([status, count]) => {
    tabs.push({ label: status, value: status, count });
  });

  return tabs;
};

export default LoanDrilldownDocumentsTab;
